import { Box, FormLabel, HStack, Text, VStack } from '@elkaso-app/web-design';
import React, { memo, useCallback, useState } from 'react';
import { GoogleMap, Marker, withGoogleMap, withScriptjs } from 'react-google-maps';
import { FiMapPin } from 'react-icons/fi';
interface InteractiveMapProps {
  lat: number;
  lng: number;
  onCoordinatesChange: (lat: number, lng: number) => void;
  title?: string;
  height?: string;
  zoom?: number;
}

// Default center for Saudi Arabia
const DEFAULT_CENTER = {
  lat: 24.7136,
  lng: 46.6753,
};

// Custom marker icon
const MARKER_ICON = {
  path: 'M15.3579 35.6744C15.3579 19.8548 28.2724 10.25 40.6254 10.25C52.9783 10.25 65.8928 19.8548 65.8928 35.6744C65.8928 50.6465 43.4328 73.5285 42.5906 74.3759C42.0291 74.9409 41.4676 75.2234 40.6254 75.2234C39.7831 75.2234 39.2216 74.9409 38.6601 74.3759C37.8179 73.5285 15.3579 50.6465 15.3579 35.6744ZM40.9999 49.0536C48.2782 49.0536 54.1785 42.9894 54.1785 35.5089C54.1785 28.0284 48.2782 21.9643 40.9999 21.9643C33.7216 21.9643 27.8213 28.0284 27.8213 35.5089C27.8213 42.9894 33.7216 49.0536 40.9999 49.0536Z',
  fillColor: '#3182CE',
  fillOpacity: 1,
  anchor: { x: 41, y: 78 },
  strokeWeight: 0,
  scale: 0.8,
};

const GoogleMapComponent = withScriptjs(
  withGoogleMap((props: any) => {
    const { lat, lng, onCoordinatesChange, title, zoom = 15 } = props;

    const center = {
      lat: lat || DEFAULT_CENTER.lat,
      lng: lng || DEFAULT_CENTER.lng,
    };

    const handleMapClick = useCallback(
      (event: any) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();
        onCoordinatesChange(newLat, newLng);
      },
      [onCoordinatesChange]
    );

    const handleMarkerDragEnd = useCallback(
      (event: any) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();
        onCoordinatesChange(newLat, newLng);
      },
      [onCoordinatesChange]
    );

    return (
      <GoogleMap
        zoom={zoom}
        center={center}
        onClick={handleMapClick}
        defaultOptions={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'on' }],
            },
          ],
        }}>
        <Marker
          position={center}
          title={title}
          icon={MARKER_ICON as any}
          draggable={true}
          onDragEnd={handleMarkerDragEnd}
        />
      </GoogleMap>
    );
  })
);

export const InteractiveMap: React.FC<InteractiveMapProps> = memo(
  ({ lat, lng, onCoordinatesChange, title, height, zoom }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleCoordinatesChange = useCallback(
      (newLat: number, newLng: number) => {
        setIsDragging(true);
        onCoordinatesChange(newLat, newLng);
        setTimeout(() => setIsDragging(false), 100);
      },
      [onCoordinatesChange]
    );

    if (!lat || !lng) {
      return (
        <VStack spacing={3}>
          <Box
            height={height || '400px'}
            borderRadius='md'
            bg='gray.100'
            display='flex'
            alignItems='center'
            justifyContent='center'
            border='1px'
            borderColor='gray.200'>
            <VStack spacing={2}>
              <FiMapPin size={32} color='#9CA3AF' />
              <Text color='gray.500' fontSize='sm' textAlign='center'>
                Enter coordinates to display map
                <br />
                Click on map or drag marker to set location
              </Text>
            </VStack>
          </Box>
        </VStack>
      );
    }

    return (
      <VStack spacing={3} align='stretch'>
        <FormLabel mb={0}>
          <HStack spacing={2}>
            <FiMapPin color='#4A5568' />
            <Text fontSize='sm' fontWeight='semibold'>
              Interactive Map - Click or drag marker to update coordinates
            </Text>
          </HStack>
        </FormLabel>

        <Box border='1px' borderColor='gray.200' borderRadius='md' overflow='hidden' position='relative'>
          {isDragging && (
            <Box
              position='absolute'
              top={2}
              right={2}
              bg='blue.500'
              color='white'
              px={2}
              py={1}
              borderRadius='md'
              fontSize='xs'
              zIndex={1000}>
              Updating location...
            </Box>
          )}

          <GoogleMapComponent
            googleMapURL={`https://maps.googleapis.com/maps/api/js?key=AIzaSyD0eC_j5MVeV5YsOIRnLVRyIx9npurleUA&region=SA&libraries=places&language=en`}
            loadingElement={<div style={{ height: `100%` }} />}
            containerElement={<div style={{ height: height || '400px', borderRadius: '8px', overflow: 'hidden' }} />}
            mapElement={<div style={{ height: `100%` }} />}
            {...({ lat, lng, onCoordinatesChange: handleCoordinatesChange, title, zoom } as any)}
          />
        </Box>

        <HStack spacing={4} fontSize='sm' color='gray.600'>
          <Text>
            <strong>Current Coordinates:</strong> {lat.toFixed(6)}, {lng.toFixed(6)}
          </Text>
        </HStack>
      </VStack>
    );
  }
);

InteractiveMap.displayName = 'InteractiveMap';
